import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../services/api';
import { localDateStr } from '../utils/dateUtils';
import Modal from '../components/common/Modal';
import Pagination from '../components/common/Pagination';
import { generateInvoicePDF, generateSingleInvoicePDF, generateProformaInvoicePDF } from '../utils/pdfGenerator';
import { useDebounce } from '../hooks/useDebounce';
import { useAuth } from '../context/AuthContext';

const STATUSES = ['pending','confirmed','processing','shipped','delivered','cancelled','on_hold'];
const statusColors = { pending:'warning', confirmed:'info', processing:'info', shipped:'primary', delivered:'success', cancelled:'error', on_hold:'neutral' };

const DownloadIcon = () => (
  <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
  </svg>
);

const EyeIcon = () => (
  <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
  </svg>
);

function DownloadDropdown({ order }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const loadAndGenerate = async (fn) => {
    setOpen(false);
    const { data: res } = await api.get(`/orders/${order.id}`);
    fn(res.data);
  };

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        className="btn btn-ghost btn-icon"
        data-tooltip="Download PDF"
        onClick={() => setOpen(o => !o)}
      >
        <DownloadIcon />
      </button>
      {open && (
        <div style={{
          position: 'absolute', right: 0, top: '100%', zIndex: 9999,
          background: '#fff', border: '1px solid var(--border)', borderRadius: 8,
          boxShadow: '0 4px 20px rgba(0,0,0,0.12)', minWidth: 230, padding: '4px 0',
        }}>
          <div style={{ padding: '6px 12px 4px', fontSize: '0.72rem', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Tax Invoice
          </div>
          {[
            { label: 'All 3 Copies (Orig + Dup + Trip)', fn: o => generateInvoicePDF(o, { copies: ['ORIGINAL', 'DUPLICATE', 'TRIPLICATE'] }) },
            { label: 'Original only', fn: o => generateSingleInvoicePDF(o, 'ORIGINAL') },
            { label: 'Duplicate only', fn: o => generateSingleInvoicePDF(o, 'DUPLICATE') },
            { label: 'Triplicate only', fn: o => generateSingleInvoicePDF(o, 'TRIPLICATE') },
          ].map(item => (
            <button key={item.label}
              style={{
                display: 'block', width: '100%', textAlign: 'left',
                padding: '8px 12px', background: 'none', border: 'none',
                cursor: 'pointer', fontSize: '0.84rem', color: 'var(--text-primary)',
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--primary-lighter)'}
              onMouseLeave={e => e.currentTarget.style.background = 'none'}
              onClick={() => loadAndGenerate(item.fn)}
            >
              {item.label}
            </button>
          ))}
          <div style={{ margin: '4px 12px', borderTop: '1px solid var(--border)' }} />
          <div style={{ padding: '6px 12px 4px', fontSize: '0.72rem', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Proforma Invoice
          </div>
          <button
            style={{
              display: 'block', width: '100%', textAlign: 'left',
              padding: '8px 12px', background: 'none', border: 'none',
              cursor: 'pointer', fontSize: '0.84rem', color: '#c2410c',
            }}
            onMouseEnter={e => e.currentTarget.style.background = '#fff7ed'}
            onMouseLeave={e => e.currentTarget.style.background = 'none'}
            onClick={() => loadAndGenerate(o => generateProformaInvoicePDF(o))}
          >
            Download Proforma PDF
          </button>
        </div>
      )}
    </div>
  );
}

function PaymentLinkSection({ order, isPrivileged, onLinkGenerated }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [linkUrl, setLinkUrl] = useState(order.payment_link_url || '');
  const [copied, setCopied] = useState(false);

  const handleGenerate = async () => {
    setLoading(true);
    setError('');
    try {
      const { data: res } = await api.post(`/orders/${order.id}/payment-link`);
      if (res.success) {
        setLinkUrl(res.payment_link_url);
        if (onLinkGenerated) onLinkGenerated(res.payment_link_url);
      } else {
        setError(res.message || 'Failed to generate payment link.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to generate payment link.');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(linkUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const total = parseFloat(order.total || 0);

  if (total <= 0) return null;

  return (
    <div style={{
      marginTop: 16,
      padding: '12px 14px',
      background: 'var(--bg-secondary)',
      borderRadius: 8,
      border: '1px solid var(--border)',
    }}>
      <div style={{ fontSize: '0.72rem', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 8 }}>
        Payment Link
      </div>

      {linkUrl ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{
            flex: 1, fontSize: '0.82rem', color: 'var(--primary)',
            wordBreak: 'break-all', fontFamily: 'monospace',
          }}>
            {linkUrl}
          </span>
          <button
            className="btn btn-ghost"
            style={{ padding: '4px 10px', fontSize: '0.78rem' }}
            onClick={handleCopy}
          >
            {copied ? 'Copied!' : 'Copy'}
          </button>
          <a
            href={linkUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-primary"
            style={{ padding: '4px 10px', fontSize: '0.78rem', textDecoration: 'none' }}
          >
            Open Link
          </a>
        </div>
      ) : isPrivileged ? (
        <div>
          <button
            className="btn btn-primary"
            style={{ padding: '6px 14px', fontSize: '0.82rem' }}
            onClick={handleGenerate}
            disabled={loading}
          >
            {loading ? 'Generating…' : 'Generate Payment Link'}
          </button>
          {error && (
            <span style={{ marginLeft: 10, fontSize: '0.8rem', color: 'var(--error, #dc2626)' }}>
              {error}
            </span>
          )}
        </div>
      ) : null}
    </div>
  );
}

const PAYMENT_METHODS = [
  { value: 'bank_transfer', label: 'Bank Transfer' },
  { value: 'cheque',        label: 'Cheque' },
  { value: 'cash',          label: 'Cash' },
  { value: 'upi',           label: 'UPI' },
  { value: 'card',          label: 'Card' },
  { value: 'other',         label: 'Other' },
];

function RecordPaymentSection({ order, onRecorded }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');
  const [success, setSuccess] = useState('');
  const today = localDateStr();
  const [form, setForm] = useState({
    amount: Number(order.total || 0).toFixed(2),
    currency: order.currency || 'INR',
    payment_date: today,
    method: 'bank_transfer',
    reference: '',
    notes: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.amount || isNaN(Number(form.amount)) || Number(form.amount) <= 0) {
      setError('Please enter a valid amount.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await api.post('/accounting/payments', {
        order_id:     order.id,
        client_id:    order.client_id,
        amount:       Number(form.amount),
        currency:     form.currency,
        payment_date: form.payment_date,
        method:       form.method,
        reference:    form.reference || undefined,
        notes:        form.notes || undefined,
      });
      setSuccess('Payment recorded successfully.');
      setOpen(false);
      if (onRecorded) onRecorded();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to record payment.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ marginTop: 12, borderTop: '1px solid var(--border)', paddingTop: 12 }}>
      {success && (
        <div style={{ marginBottom: 8, padding: '7px 12px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 6, fontSize: '0.83rem', color: '#166534' }}>
          {success}
        </div>
      )}
      {!open ? (
        <button
          className="btn btn-primary"
          style={{ padding: '7px 16px', fontSize: '0.83rem' }}
          onClick={() => { setOpen(true); setSuccess(''); }}
        >
          Record Payment
        </button>
      ) : (
        <form onSubmit={handleSubmit}>
          <div style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 10 }}>
            Record Payment
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 16px' }}>
            <div>
              <label style={{ fontSize: '0.76rem', fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 3 }}>Amount *</label>
              <input
                type="number" step="0.01" min="0.01" required
                className="form-control"
                value={form.amount}
                onChange={e => setForm(f => ({ ...f, amount: e.target.value }))}
              />
            </div>
            <div>
              <label style={{ fontSize: '0.76rem', fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 3 }}>Payment Date *</label>
              <input
                type="date" required
                className="form-control"
                value={form.payment_date}
                onChange={e => setForm(f => ({ ...f, payment_date: e.target.value }))}
              />
            </div>
            <div>
              <label style={{ fontSize: '0.76rem', fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 3 }}>Method</label>
              <select className="form-control form-select" value={form.method} onChange={e => setForm(f => ({ ...f, method: e.target.value }))}>
                {PAYMENT_METHODS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize: '0.76rem', fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 3 }}>Reference / UTR</label>
              <input
                type="text"
                className="form-control"
                placeholder="e.g. UTR or cheque no."
                value={form.reference}
                onChange={e => setForm(f => ({ ...f, reference: e.target.value }))}
              />
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{ fontSize: '0.76rem', fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 3 }}>Notes</label>
              <input
                type="text"
                className="form-control"
                placeholder="Optional notes"
                value={form.notes}
                onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              />
            </div>
          </div>
          {error && (
            <div style={{ marginTop: 8, color: '#dc2626', fontSize: '0.8rem' }}>{error}</div>
          )}
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button type="submit" className="btn btn-primary" style={{ padding: '7px 18px', fontSize: '0.83rem' }} disabled={saving}>
              {saving ? 'Saving…' : 'Save Payment'}
            </button>
            <button type="button" className="btn btn-ghost" style={{ padding: '7px 14px', fontSize: '0.83rem' }} onClick={() => setOpen(false)}>
              Cancel
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

function OrderDetailModal({ order, onClose, onLinkGenerated }) {
  const { isPrivileged } = useAuth();
  if (!order) return null;
  const items = order.items || [];
  return (
    <Modal open onClose={onClose} title={`Invoice — ${order.invoice_number || order.order_number}`} size="lg">
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 24px', marginBottom: 16 }}>
        {[
          ['Invoice #', order.invoice_number || <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>Assigned on delivery</span>],
          ['Order #', order.order_number],
          ['Client', order.client_name],
          ['Status', <span key="s" className={`badge badge-${statusColors[order.status]||'neutral'}`}>{order.status}</span>],
          ['Quote #', order.quote_number || '—'],
          ['Quote Date', order.quote_date ? new Date(order.quote_date).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' }) : '—'],
          ['PO Number', order.po_number || '—'],
          ['PO Date', order.po_date ? new Date(order.po_date).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' }) : '—'],
          ['Currency', order.currency || 'USD'],
          ['Expected Delivery', order.expected_delivery ? new Date(order.expected_delivery).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' }) : '—'],
          ['Created', new Date(order.created_at).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' })],
        ].map(([label, value]) => (
          <div key={label}>
            <div style={{ fontSize: '0.72rem', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 2 }}>{label}</div>
            <div style={{ fontSize: '0.88rem', color: 'var(--text-primary)' }}>{value}</div>
          </div>
        ))}
      </div>
      {order.notes && (
        <div style={{ marginBottom: 12, padding: '8px 12px', background: 'var(--bg-secondary)', borderRadius: 6, fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
          <strong>Notes:</strong> {order.notes}
        </div>
      )}
      <table className="table" style={{ marginBottom: 0 }}>
        <thead><tr><th>#</th><th>Item</th><th>Pack</th><th>Qty</th><th>Price</th><th>Disc%</th><th>Total</th></tr></thead>
        <tbody>
          {items.map((it, i) => (
            <tr key={it.id}>
              <td>{i + 1}</td>
              <td style={{ fontWeight: 500 }}>{it.item_name || it.catalog_item_name || '—'}</td>
              <td>{it.pack_size || '—'}</td>
              <td style={{ textAlign: 'center' }}>{it.quantity}</td>
              <td style={{ textAlign: 'right' }}>{order.currency} {Number(it.price).toFixed(2)}</td>
              <td style={{ textAlign: 'center' }}>{it.discount_percent || 0}%</td>
              <td style={{ textAlign: 'right', fontWeight: 600 }}>{order.currency} {Number(it.total).toFixed(2)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div style={{ marginTop: 12, textAlign: 'right' }}>
        <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginRight: 16 }}>
          Subtotal: <strong>{order.currency} {Number(order.subtotal || 0).toFixed(2)}</strong>
          {Number(order.discount_percent) > 0 && <> | Discount ({order.discount_percent}%): <strong>-{order.currency} {Number(order.discount_amount || 0).toFixed(2)}</strong></>}
        </span>
        <span style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--primary)' }}>
          Total: {order.currency} {Number(order.total || 0).toFixed(2)}
        </span>
      </div>
      <PaymentLinkSection order={order} isPrivileged={isPrivileged} onLinkGenerated={onLinkGenerated} />
      {isPrivileged && <RecordPaymentSection order={order} onRecorded={onClose} />}
    </Modal>
  );
}

export default function Invoices() {
  const [data, setData] = useState({ data: [], total: 0, page: 1, limit: 15 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [page, setPage] = useState(1);
  const [viewOrder, setViewOrder] = useState(null);

  const debouncedSearch = useDebounce(search, 300);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: res } = await api.get('/orders', {
        params: {
          order_type: 'invoice',
          page,
          limit: 15,
          search: debouncedSearch || undefined,
          status: statusFilter || undefined,
          from_date: fromDate || undefined,
          to_date: toDate || undefined,
        },
      });
      setData(res);
    } finally { setLoading(false); }
  }, [page, debouncedSearch, statusFilter, fromDate, toDate]);

  useEffect(() => { load(); }, [load]);

  const openView = async (o) => {
    const { data: res } = await api.get(`/orders/${o.id}`);
    setViewOrder(res.data);
  };

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Invoices</h1>
          <p className="page-subtitle">Download tax invoices in Original, Duplicate &amp; Triplicate copies</p>
        </div>
      </div>

      {/* Filters */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-body" style={{ padding: '14px 16px' }}>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
            <div className="search-input-wrap" style={{ flex: '1 1 220px' }}>
              <span className="search-icon">
                <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
              </span>
              <input className="form-control" placeholder="Search invoice # or client…" value={search}
                onChange={e => { setSearch(e.target.value); setPage(1); }} />
            </div>
            <select className="form-control form-select" style={{ width: 150 }} value={statusFilter}
              onChange={e => { setStatusFilter(e.target.value); setPage(1); }}>
              <option value="">All Status</option>
              {STATUSES.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
            </select>
            <input type="date" className="form-control" style={{ width: 150 }} value={fromDate}
              onChange={e => { setFromDate(e.target.value); setPage(1); }} title="From date" />
            <input type="date" className="form-control" style={{ width: 150 }} value={toDate}
              onChange={e => { setToDate(e.target.value); setPage(1); }} title="To date" />
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card" style={{ overflow: 'visible' }}>
        <div className="table-container" style={{ overflowX: 'auto', overflowY: 'visible' }}>
          <table className="table">
            <thead>
              <tr>
                <th>Invoice #</th>
                <th>Client</th>
                <th>Date</th>
                <th>Delivery</th>
                <th>Amount</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={7}><div className="loading-overlay"><div className="spinner" /></div></td></tr>
              )}
              {!loading && data.data.length === 0 && (
                <tr><td colSpan={7}><div className="empty-state"><p>No invoices found.</p></div></td></tr>
              )}
              {!loading && data.data.map(o => (
                <tr key={o.id}>
                  <td style={{ fontWeight: 700, color: 'var(--primary)', fontSize: '0.82rem' }}>
                    {o.invoice_number || o.order_number}
                    {o.invoice_number && <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)', fontWeight: 400 }}>{o.order_number}</div>}
                  </td>
                  <td>
                    <div style={{ fontWeight: 600 }}>{o.client_name}</div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{o.client_country || ''}</div>
                  </td>
                  <td style={{ color: 'var(--text-muted)', fontSize: '0.82rem' }}>
                    {new Date(o.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                  </td>
                  <td style={{ color: 'var(--text-muted)', fontSize: '0.82rem' }}>
                    {o.expected_delivery ? new Date(o.expected_delivery).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                  </td>
                  <td style={{ fontWeight: 700 }}>
                    <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>{o.currency} </span>
                    {Number(o.total || 0).toFixed(2)}
                  </td>
                  <td>
                    <span className={`badge badge-${statusColors[o.status] || 'neutral'}`} style={{ textTransform: 'capitalize' }}>
                      {o.status}
                    </span>
                  </td>
                  <td>
                    <div className="table-actions">
                      <button className="btn btn-ghost btn-icon" data-tooltip="View Details" onClick={() => openView(o)}>
                        <EyeIcon />
                      </button>
                      <DownloadDropdown order={o} />
                      <button
                        className="btn btn-ghost btn-icon"
                        data-tooltip="Share on WhatsApp"
                        style={{ color: '#25d366' }}
                        onClick={() => {
                          const amt = `${o.currency} ${Number(o.total || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                          const recipientName = o.client_name || o.customer_name || 'Customer';
                          const msg = `Dear ${recipientName}, please find your invoice #${o.invoice_number || o.order_number} from PRASI LABS. Amount: ${amt}. View/download on request.`;
                          window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank');
                        }}
                      >
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination page={data.page} total={data.total} limit={data.limit} onPageChange={setPage} />
      </div>

      {viewOrder && (
        <OrderDetailModal
          order={viewOrder}
          onClose={() => setViewOrder(null)}
          onLinkGenerated={(url) => setViewOrder(prev => prev ? { ...prev, payment_link_url: url } : prev)}
        />
      )}
    </div>
  );
}
